import React, { useState, useEffect, useRef } from 'react';
import { Plus, User, UserPlus, Users, Building2, Search, SlidersHorizontal } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import ProjectCard from '../components/ProjectCard';
import ProjectModal from '../components/ProjectModal';
import DeleteProjectModal from '../components/DeleteProjectModal';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { useProjectStore } from '../../../stores/projectsSlice';
import { projectService } from '..';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { hasPermission } from '../../../utils/permissions';

const STATUS_ALIASES = {
  inProgress: 'in_progress',
  onHold: 'on_hold',
};

const normalizeStatus = (value) => {
  if (!value) return '';
  return STATUS_ALIASES[value] || value;
};

const getUserId = (user) => user?.id || user?.uid || user?.user_id || user?.sub || null;

const getProjectCreatorId = (project) => {
  const createdBy = project?.createdBy;
  if (typeof createdBy === 'object' && createdBy) {
    return createdBy.id || createdBy.user_id || createdBy.uid || null;
  }

  return (
    project?.created_by ||
    project?.created_by_id ||
    project?.createdBy ||
    project?.user_id ||
    project?.owner_id ||
    project?.ownerId ||
    null
  );
};

const getProjectManagerId = (project) =>
  project?.project_manager_id ||
  project?.project_manager ||
  project?.projectManager ||
  project?.user_id ||
  getProjectCreatorId(project);

const Projects = () => {
  const { user } = useApiAuth();
  const currentUserId = getUserId(user);
  const navigate = useNavigate();
  const projects = useProjectStore(state => state.projects);
  const setProjects = useProjectStore(state => state.setProjects);
  const addProject = useProjectStore(state => state.addProject);
  const updateProject = useProjectStore(state => state.updateProject);
  const deleteProject = useProjectStore(state => state.deleteProject);
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const mobileFilterRef = useRef(null);

  // Check permissions from user object
  const userRole = user?.role || '';
  const canCreateProject = hasPermission(user, 'projects.create');

  const canDeleteProject = (project) => {
    if (userRole === 'admin') {
      return true;
    }

    const creatorId = getProjectCreatorId(project);
    return Boolean(creatorId && currentUserId && String(creatorId) === String(currentUserId));
  };
  
  // Ownership options for the filter
  const ownershipOptions =
    userRole === 'client'
      ? [
          { value: 'client', label: 'My Projects', icon: Building2 }
        ]
      : [
          { value: 'all', label: 'All Projects', icon: Users },
          { value: 'owned', label: 'Managing', icon: User },
          { value: 'member', label: 'Shared With Me', icon: UserPlus },
          { value: 'client', label: 'Client Projects', icon: Building2 }
        ];

  useEffect(() => {
    if (userRole === 'client') setOwnershipFilter('client');
  }, [userRole]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const close = (e) => {
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(e.target))
        setMobileFiltersOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [mobileFiltersOpen]);

  const projectsList = Array.isArray(projects)
    ? projects
    : projects && typeof projects === 'object'
      ? Object.values(projects)
      : [];

  // Filter and sort projects
  const filteredProjects = projectsList
    .filter((project) => {
      const normalizedProjectStatus = normalizeStatus(project?.status);
      const normalizedSelectedStatus = normalizeStatus(statusFilter);
      const statusMatch =
        statusFilter === 'all' ? true : normalizedProjectStatus === normalizedSelectedStatus;
    
      let ownershipMatch = true;
      const projectManagerId = getProjectManagerId(project);
      const members = Array.isArray(project?.members) && project.members.length > 0 ? project.members : project?.team || [];
      const isMember = members.some(member => String(member?.user_id || member?.id) === String(currentUserId));

      if (ownershipFilter !== 'all') {
        if (ownershipFilter === 'owned') {
          ownershipMatch = String(projectManagerId) === String(currentUserId);
        } else if (ownershipFilter === 'member') {
          ownershipMatch = String(projectManagerId) !== String(currentUserId) && isMember;
        } else if (ownershipFilter === 'client') {
          ownershipMatch =
            String(project?.client_id) === String(currentUserId) ||
            (Array.isArray(project?.client_ids) && project.client_ids.map(String).includes(String(currentUserId)));
        }
      }

      const query = searchQuery.trim().toLowerCase();
      const searchableText = [
        project?.name,
        project?.description,
        project?.status,
        members.map(member => member?.name || member?.email).join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch = query.length === 0 || searchableText.includes(query);
    
      return statusMatch && ownershipMatch && searchMatch;
    })
    .sort((a, b) => {
      const dateA = new Date(a?.updatedAt || a?.updated_at || a?.createdAt || a?.created_at || 0).getTime();
      const dateB = new Date(b?.updatedAt || b?.updated_at || b?.createdAt || b?.created_at || 0).getTime();

      if (sortBy === 'updated_asc') {
        return dateA - dateB;
      }

      if (sortBy === 'name_asc') {
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      }

      if (sortBy === 'name_desc') {
        return String(b?.name || '').localeCompare(String(a?.name || ''));
      }

      return dateB - dateA;
    });

  // Status options for the filter
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'planning', label: 'Planning' },
    { value: 'active', label: 'Active' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    let isMounted = true;

    const loadProjects = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load all projects from API
        const response = await projectService.getProjects();
        const projectsData = response.projects || [];
        
        if (isMounted) {
          setProjects(projectsData);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        if (isMounted) {
          setError('Failed to load projects. Please try again.');
          toast.error('Failed to load projects');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, [user, setProjects]);

  // Handle loading state
  if (loading) {
    return (
      <div className="mx-auto my-10 w-full max-w-4xl rounded-2xl border p-6" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2" style={{ borderTopColor: BRAND_COLORS.primary, borderColor: SYSTEM_COLORS.borderSoft }} />
          <span className="text-base font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
            Loading projects...
          </span>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="mb-4" style={{ color: SYSTEM_COLORS.danger }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-white rounded-lg"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          Retry
        </button>
      </div>
    );
  }

  const handleProjectClick = (project) => {
    if (!project?.id) return;
    navigate(`/projects/${project.id}`);
  };

  const handleDeleteClick = (project) => {
    if (!project) return;

    if (!canDeleteProject(project)) {
      toast.error('Only admins or project creators can delete this project');
      return;
    }

    setSelectedProject(project);
    setIsDeleteModalOpen(true);
  };

  const handleSaveProject = async (projectData) => {
    try {
      if (selectedProject) {
        const updated = await projectService.patchProject(selectedProject.id, projectData);
        updateProject(selectedProject.id, { ...selectedProject, ...updated });
        toast.success('Project updated successfully');
      } else {
        const createPayload = {
          ...projectData,
          created_by: projectData?.created_by || currentUserId,
        };
        const newProject = await projectService.createProject(createPayload);
        addProject(newProject);
        toast.success('Project created successfully');
      }
      setIsModalOpen(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    if (!canDeleteProject(selectedProject)) {
      toast.error('Only admins or project creators can delete this project');
      return;
    }

    try {
      await projectService.removeProject(selectedProject.id);
      deleteProject(selectedProject.id);
      toast.success('Project deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>Projects</h1>
        {canCreateProject && (
          <button 
            onClick={() => {
              setSelectedProject(null);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-white shadow-sm sm:w-auto"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <Plus size={20} />
            New Project
          </button>
        )}
      </div>

      {/* Filter bar — single row */}
      {(() => {
        const activeFilterCount = [
          statusFilter !== 'all',
          ownershipFilter !== (userRole === 'client' ? 'client' : 'all'),
          sortBy !== 'updated_desc',
        ].filter(Boolean).length;

        const selectStyle = {
          borderColor: SYSTEM_COLORS.borderSoft,
          backgroundColor: SYSTEM_COLORS.shellSurface,
          color: NEUTRAL_COLORS.black,
        };

        return (
          <div
            className="flex items-center gap-2 rounded-xl border px-2 py-2 shadow-sm"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
          >
            {/* Search — always visible */}
            <div
              className="flex flex-1 items-center gap-2 rounded-lg border px-3 py-1.5 min-w-0"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
            >
              <Search size={15} style={{ color: SYSTEM_COLORS.textMuted, flexShrink: 0 }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects, status, or member…"
                className="w-full bg-transparent text-sm outline-none min-w-0"
                style={{ color: NEUTRAL_COLORS.black }}
              />
            </div>

            {/* Desktop: inline selects */}
            <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
              <select
                value={ownershipFilter}
                onChange={(e) => setOwnershipFilter(e.target.value)}
                className="rounded-lg border px-3 py-1.5 text-sm"
                style={selectStyle}
              >
                {ownershipOptions.map(o => (
                  <option key={o.value} value={o.value} disabled={userRole === 'client' && o.value !== 'client'}>
                    {o.label}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border px-3 py-1.5 text-sm"
                style={selectStyle}
              >
                {statusOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-lg border px-3 py-1.5 text-sm"
                style={selectStyle}
              >
                <option value="updated_desc">Recently Updated</option>
                <option value="updated_asc">Oldest Updated</option>
                <option value="name_asc">Name A–Z</option>
                <option value="name_desc">Name Z–A</option>
              </select>
            </div>

            {/* Mobile: filter button + dropdown */}
            <div className="relative lg:hidden flex-shrink-0" ref={mobileFilterRef}>
              <button
                onClick={() => setMobileFiltersOpen(v => !v)}
                className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors"
                style={{
                  borderColor: activeFilterCount > 0 ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft,
                  backgroundColor: activeFilterCount > 0 ? SYSTEM_COLORS.primaryTint : SYSTEM_COLORS.shellSurface,
                  color: activeFilterCount > 0 ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                }}
              >
                <SlidersHorizontal size={14} />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span
                    className="flex items-center justify-center rounded-full text-[10px] font-bold"
                    style={{ width: 16, height: 16, backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {mobileFiltersOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-64 rounded-xl border shadow-xl z-50 p-3 space-y-3"
                  style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
                >
                  {/* Ownership */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: SYSTEM_COLORS.textMuted }}>View</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {ownershipOptions.map(o => {
                        const Icon = o.icon;
                        const active = ownershipFilter === o.value;
                        return (
                          <button
                            key={o.value}
                            onClick={() => setOwnershipFilter(o.value)}
                            disabled={userRole === 'client' && o.value !== 'client'}
                            className="flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-medium"
                            style={{
                              backgroundColor: active ? SYSTEM_COLORS.primaryTint : '#f8fafc',
                              color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                              borderColor: active ? BRAND_COLORS.primary + '55' : SYSTEM_COLORS.borderSoft,
                            }}
                          >
                            <Icon size={12} />
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: SYSTEM_COLORS.textMuted }}>Status</p>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      style={selectStyle}
                    >
                      {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>

                  {/* Sort */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: SYSTEM_COLORS.textMuted }}>Sort</p>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      style={selectStyle}
                    >
                      <option value="updated_desc">Recently Updated</option>
                      <option value="updated_asc">Oldest Updated</option>
                      <option value="name_asc">Name A–Z</option>
                      <option value="name_desc">Name Z–A</option>
                    </select>
                  </div>

                  {/* Clear */}
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setOwnershipFilter(userRole === 'client' ? 'client' : 'all');
                        setSortBy('updated_desc');
                        setMobileFiltersOpen(false);
                      }}
                      className="w-full rounded-lg py-2 text-xs font-semibold"
                      style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
        Showing {filteredProjects.length} of {projectsList.length} projects
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filteredProjects.length > 0 ? (
          filteredProjects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project)}
              canDelete={canDeleteProject(project)}
              onDelete={handleDeleteClick}
            />
          ))
        ) : (
          <div className="col-span-3 py-12 flex flex-col items-center justify-center" style={{ color: SYSTEM_COLORS.textMuted }}>
            <Users size={48} className="mb-4" style={{ color: SYSTEM_COLORS.textSoft }} />
            <p className="text-lg" style={{ color: NEUTRAL_COLORS.black }}>No projects found</p>
            <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Try changing your filters or create a new project</p>
          </div>
        )}
      </div>

      {/* Project Create/Edit Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onSave={handleSaveProject}
      />

      {/* Delete Confirmation Modal */}
      <DeleteProjectModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedProject(null);
        }}
        onConfirm={handleDeleteProject}
        projectName={selectedProject?.name}
      />
    </div>
  );
};

export default Projects;
